<template>
  <View gap="md" direction="column" :grow="1">
    <ScrollArea>
      <View gap="md" padding-block="sm" role="tablist">
        <Text
          variant="h3"
          as="button"
          class="section-tab"
          role="tab"
          wrap="nowrap"
          :tabindex="0"
          :aria-selected="activeSection === section.label"
          v-for="section in sections"
          :color="activeSection === section.label ? 'primary' : 'tertiary'"
          @click="onSelectTab(section.label)"
        >
          <div>{{ section.label }}</div>
          <div class="indicator" :class="{ active: section.indicator }"></div>
        </Text>
      </View>
    </ScrollArea>

    <div class="section-content">
      <ShowingRequestsSection
        v-if="activeSection === Section.SHOWING_REQUESTS"
        :is-loading="showingsQuery.isPending.value"
        :pending-showings="pendingShowings"
      />

      <IntelligentEscalationsSection
        v-if="activeSection === Section.INTELLIGENT_ESCALATIONS"
        :is-loading="intelligentEscalationsQuery.isPending.value"
        :escalations="intelligentEscalations"
      />

      <ApplicationsSection
        v-if="activeSection === Section.APPLICATIONS"
        :is-loading="applicationBundlesLoading"
        :application-bundles="applicationBundles"
        :is-wants-to-apply-loading="wantsToApplyQuery.isPending.value"
        :wants-to-apply="wantsToApply"
      />
    </div>
  </View>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { ApplicationBundleStatus } from '@tallo/applications';
import { ScrollArea, Text, View } from '@tallo/design-system';
import { useApplicationBundles } from '@tallo/investor/applications';
import { useIntelligentEscalationsQuery } from '@tallo/investor/intelligent-escalations';
import { useCompanyWantsToApplyQuery } from '@tallo/investor/property';
import { useShowingsQuery } from '@tallo/investor/showing';

import ApplicationsSection from './sections/applications-section.vue';
import IntelligentEscalationsSection from './sections/intelligent-escalations-section.vue';
import ShowingRequestsSection from './sections/showing-requests-section.vue';

enum Section {
  INTELLIGENT_ESCALATIONS = 'Intelligent escalations',
  SHOWING_REQUESTS = 'Showing requests',
  APPLICATIONS = 'Applications',
}

const showingsQuery = useShowingsQuery({ onlyWithPendingShowingReqests: true });
const intelligentEscalationsQuery = useIntelligentEscalationsQuery();
const { applicationBundles, isPending: applicationBundlesLoading } = useApplicationBundles();
const wantsToApplyQuery = useCompanyWantsToApplyQuery();

const pendingShowings = computed(() => showingsQuery.data.value || []);
const intelligentEscalations = computed(() => intelligentEscalationsQuery.data?.value || []);
const wantsToApply = computed(() => wantsToApplyQuery.data.value || []);
const filteredApplicationBundles = computed(() =>
  applicationBundles.value.filter((bundle) => {
    return [
      ApplicationBundleStatus.SUBMITTED,
      ApplicationBundleStatus.SENT,
      ApplicationBundleStatus.IN_PROGRESS,
      ApplicationBundleStatus.UPDATE_REQUESTED,
    ].includes(bundle.status);
  }),
);

const sections = computed(() => [
  {
    label: Section.INTELLIGENT_ESCALATIONS,
    indicator: intelligentEscalations.value.length > 0,
  },
  {
    label: Section.SHOWING_REQUESTS,
    indicator: pendingShowings.value.length > 0,
  },
  {
    label: Section.APPLICATIONS,
    indicator: filteredApplicationBundles.value.length > 0 || wantsToApply.value.length > 0,
  },
]);
const activeSection = ref(sections.value[0].label);

function onSelectTab(tab: Section) {
  activeSection.value = tab;
}
</script>

<style scoped lang="scss">
.indicator {
  width: var(--t-spacing-sm);
  height: var(--t-spacing-sm);
  border-radius: 50%;
  background-color: transparent;

  &.active {
    background-color: var(--t-color-orange-500);
  }
}

.section-tab {
  display: flex;
  gap: var(--t-spacing-sm);
  align-items: center;
  padding: 0;
  cursor: pointer;
  appearance: none;
  border: none;
  background: transparent;
}

.section-content {
  flex-grow: 1;
}
</style>
